import { getExtensionVersion } from '../chrome/api';
import {
  getDelegationStore,
  getDeviceAndUserStore,
  getReportedDeviceInfoStore,
} from '../storage/stores';
import {
  postDeviceDetails,
  postDeviceOptions,
  updateDeviceExtensionVersion,
} from '../api/device';
import { captureException } from '../helper/sentry';
import { getOSVersion } from '../clientIdentification';
import { APIError } from '../lib/errors/api/APIError';
import { Mutex } from 'async-mutex';

/**
 * Report device information to backend if changed since the last report.
 */
export const updateDeviceInfoIfNeeded = async () => {
  const deviceUid = getDeviceAndUserStore().get()?.device;
  if (deviceUid) {
    // Extension version
    const extensionVersion = getExtensionVersion();
    if (shouldUpdateExtensionVersion(extensionVersion)) {
      await updateExtensionVersion(deviceUid, extensionVersion);
    }

    // OS version
    const osVersion = getOSVersion();
    if (osVersion && shouldUpdateDeviceDetails(osVersion)) {
      await updateDeviceDetails(deviceUid, osVersion);
    }

    // Delegation state
    return reportDelegationStatusIfChanged(
      getDelegationStore().isDelegationStarted()
    );
  }
};

/**
 * Reports the current extension version to backend.
 *
 * @remarks
 * This function will not throw any error, but it will be logged and reported.
 */
const updateExtensionVersion = async (
  deviceUid: string,
  extensionVersion: string
) => {
  try {
    await updateDeviceExtensionVersion(deviceUid, extensionVersion);
    return getReportedDeviceInfoStore().set({ extensionVersion });
  } catch (error) {
    console.error(error);
    captureException(error as Error);
  }
};

/**
 * Reports the device details to backend. That includes the ChromeOS version.
 *
 * @remarks
 * This function will not throw any error, but it will be logged and reported.
 */
const updateDeviceDetails = async (deviceUid: string, osVersion: string) => {
  try {
    await postDeviceDetails(deviceUid, osVersion);
    return getReportedDeviceInfoStore().set({ osVersion });
  } catch (error) {
    console.error(error);
    captureException(error as Error);
  }
};

const shouldUpdateExtensionVersion = (extensionVersion: string) =>
  extensionVersion !== getReportedDeviceInfoStore().get()?.extensionVersion;

const shouldUpdateDeviceDetails = (osVersion: string) =>
  osVersion !== getReportedDeviceInfoStore().get()?.osVersion;

const DELEGATION_STARTED_DEVICE_OPTIONS_KEY = 'isDelegationStarted';

/**
 * Reports the device delegation status to QAPI as device options if it has changed since the last successful report.
 *
 * Since this can be called from different places at once, it makes sure the next call waits the previous one to avoid race conditions.
 * As that could lead to reporting the same value twice.
 *
 * @param isDelegationStarted - A boolean indicating the current delegation status of the device.
 * @returns A Promise that resolves if the delegation status was reported successfully, or `void`
 * if the device or user UIDs are missing or the delegation status has not changed.
 */
const reportDelegationStatusIfChangedMutex = new Mutex();
export const reportDelegationStatusIfChanged = async (
  isDelegationStarted: boolean
) =>
  reportDelegationStatusIfChangedMutex.runExclusive(async () => {
    const { device: deviceUid, user: userUid } =
      getDeviceAndUserStore().get() || {};
    if (
      deviceUid &&
      userUid &&
      shouldReportDelegationStatus(isDelegationStarted)
    ) {
      try {
        await postDeviceOptions(
          deviceUid,
          userUid,
          DELEGATION_STARTED_DEVICE_OPTIONS_KEY,
          isDelegationStarted.toString()
        );
        return getReportedDeviceInfoStore().set({ isDelegationStarted });
      } catch (error) {
        console.error(error);
        if (!(error instanceof APIError)) {
          captureException(error as Error);
        }
      }
    }
  });

const shouldReportDelegationStatus = (isDelegationStarted: boolean) =>
  isDelegationStarted !==
  getReportedDeviceInfoStore().get()?.isDelegationStarted;
